import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/data/site";
import { Seo } from "@/components/Seo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await register(form.name, form.email, form.password); navigate("/dashboard"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      <Seo title="Opprett konto – Helserebellen" description="Opprett en konto for å få tilgang til planene." path="/register" noindex />
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-olive/60 text-sm hover:text-terra mb-10"><ArrowLeft size={16} /> Til forsiden</Link>
          <img src={ASSETS.logo} alt="" className="w-16 mix-blend-multiply" />
          <h1 className="mt-6 font-serif text-5xl text-olive-deep">Bli rebell</h1>
          <p className="mt-2 text-olive/60">Opprett en konto for å få tilgang til planene.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <input required placeholder="Navn" value={form.name} data-testid="register-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-2xl border border-olive/20 bg-white/60 px-5 py-4 focus:outline-none focus:border-terra transition-colors" />
            <input required type="email" placeholder="E-post" value={form.email} data-testid="register-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-2xl border border-olive/20 bg-white/60 px-5 py-4 focus:outline-none focus:border-terra transition-colors" />
            <input required type="password" placeholder="Passord (min. 6 tegn)" value={form.password} data-testid="register-password"
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-2xl border border-olive/20 bg-white/60 px-5 py-4 focus:outline-none focus:border-terra transition-colors" />
            {error && <p className="text-terra text-sm" data-testid="register-error">{error}</p>}
            <button disabled={loading} data-testid="register-submit"
              className="w-full rounded-full bg-terra px-6 py-4 text-cream uppercase text-[13px] tracking-[0.2em] hover:bg-olive-deep transition-colors disabled:opacity-60">
              {loading ? "Oppretter…" : "Opprett konto"}
            </button>
          </form>
          <p className="mt-6 text-olive/60 text-sm">Har du konto? <Link to="/login" className="text-terra underline">Logg inn</Link></p>
        </motion.div>
      </div>
      <div className="hidden lg:block relative overflow-hidden bg-olive-deep order-1 lg:order-2">
        <img src={ASSETS.tips} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-olive-deep via-olive-deep/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-cream">
          <p className="font-script text-4xl text-sage">Kunnskap · Frihet · Vitalitet</p>
          <p className="font-serif text-3xl mt-3 leading-tight">Du trenger ikke være perfekt. Du trenger en plan.</p>
        </div>
      </div>
    </div>
  );
}
